import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  mergeMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsStoreService } from '@libs/src/groups/groups-store/groups-store.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ListValuesDialogService } from '@libs/src/list-values-dialog/list-values-dialog.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { GroupCustomPrivateSegmentsDrawerService } from '@main-client/src/app/group-custom-private-segments/group-custom-private-segments-drawer/group-custom-private-segments-drawer.service';
import { GroupCustomSegmentsDrawerService } from '@main-client/src/app/group-custom-segments-settings/group-custom-segments-drawer/group-custom-segments-drawer.service';
import { HubConfigRolesDrawerService } from '@main-client/src/app/hub-configuration/hub-config-roles/hub-config-roles-drawer/hub-config-roles-drawer.service';
import { PopulateSegmentValuesDrawerService } from '@main-client/src/app/members/automated-user-management/populate-segment-values-drawer/populate-segment-values-drawer.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { GroupsState } from '@libs/src/groups/groups-store/groups.interfaces';
import {
  Group,
  GroupCustomSegmentType,
  GroupCustomSegmentTypes,
  GroupRole,
} from '@libs/src/models/group.model';
import {
  accountFieldOptions,
  accountFields,
  AttributeSelection,
  findCustomSegment,
  groupFieldOptions,
  groupFields,
  GroupMappingOptions,
  MappingOption,
  role,
  SelectionType,
  VisibleUniqueColumn,
} from '@main-client/src/app/attribute-mapping/attribute-mapping.helper';
import { PrivateSegmentsDrawerData } from '@main-client/src/app/group-custom-private-segments/group-custom-private-segments-drawer/group-custom-private-segments-drawer.helper';
import {
  AttributeMapping,
  AttributeMappingPostBody,
  AttributeMappingPutBody,
  ValueTransformations,
} from '@main-client/src/app/invite-csv/invite-csv-view/invite-csv-view.interface';

import chunk from 'lodash-es/chunk';
import differenceBy from 'lodash-es/differenceBy';
import isEmpty from 'lodash-es/isEmpty';
import last from 'lodash-es/last';
import times from 'lodash-es/times';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-attribute-mapping-drawer',
  templateUrl: './attribute-mapping-drawer.component.html',
})
export class AttributeMappingDrawerComponent implements OnInit {
  @Input() group: Group;
  @Input() hasSourcePathChanged: boolean;
  @Input() importedColumn: VisibleUniqueColumn;
  attributeSelection: AttributeSelection;
  groupOptions: GroupMappingOptions;
  hasBeenUpdated = false;
  hasMappedAllViableValues = false;
  hasUnmappedValues = true;
  isAutomaticallyMappedField = false;
  pageIndex = 1;
  selected: MappingOption;
  valueOptions: MappingOption[];
  valueSelections: MappingOption[][];
  valueSelectionTableIndex = 0;
  dropDownLabels = {
    account_fields: $localize`:@@attributeMappingDrawer.renderTemplate.accountFields:Account fields`,
    custom_segments: $localize`:@@attributeMappingDrawer.renderTemplate.customSegments:Segments`,
    group_fields: $localize`:@@attributeMappingDrawer.renderTemplate.groupFields:Group fields`,
    hidden_custom_segments: $localize`:@@attributeMappingDrawer.renderTemplate.hiddenCustomSegments:Private segments`,
  };

  constructor(
    private readonly populateSegmentValuesDrawerService: PopulateSegmentValuesDrawerService,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly drawerRef: NzDrawerRef,
    private readonly groupCustomSegmentsDrawerService: GroupCustomSegmentsDrawerService,
    private readonly groupCustomPrivateSegmentsDrawerService: GroupCustomPrivateSegmentsDrawerService,
    private readonly groupsBackend: GroupsBackend,
    private readonly groupsStoreService: GroupsStoreService,
    private readonly hubConfigRolesDrawerService: HubConfigRolesDrawerService,
    private readonly listValuesDialog: ListValuesDialogService,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit(): void {
    const segmentOptions = (this.group?.custom_segments ?? []).map(
      (customSegment) => ({
        label: customSegment.title,
        value: customSegment._id,
      }),
    );
    const hiddenSegmentOptions = (this.group?.hidden_custom_segments ?? []).map(
      (customSegment) => ({
        label: customSegment.title,
        value: customSegment._id,
      }),
    );
    this.sortUnmappedValues();
    this.groupOptions = {
      account_fields: accountFieldOptions,
      custom_segments: segmentOptions,
      group_fields: groupFieldOptions,
      hidden_custom_segments: hiddenSegmentOptions,
    };
    if (this.importedColumn.translation) {
      this.attributeSelection = {
        attributeId: this.importedColumn.attributeId,
        translation: this.importedColumn.translation,
        values: this.importedColumn.values,
      };
      this.selected = [
        ...this.groupOptions.group_fields,
        ...this.groupOptions.account_fields,
        ...this.groupOptions.custom_segments,
        ...this.groupOptions.hidden_custom_segments,
      ].find((segment) => this.importedColumn.translation === segment.value);
      const section = this.findSectionType(
        this.group,
        this.importedColumn.translation,
      );
      this.hasUnmappedValues =
        'account_fields' === section
          ? false
          : !this.attributeSelection.values.every(
              (value) => !isEmpty(value.translation),
            );
      this.setValueOptions(section, this.importedColumn.translation);
      const selectionValues = this.importedColumn.values.map((value) =>
        this.valueOptions.find(
          (option) => value.translation[0] === option.value,
        ),
      );
      this.valueSelections = chunk(selectionValues, 10);
    } else {
      const pages = Math.ceil(this.importedColumn.values.length / 10);
      this.valueSelections = [...times(pages, () => [])];
      this.attributeSelection = {
        translation: undefined,
        values: [],
      };
    }
  }

  sortUnmappedValues() {
    this.importedColumn.values.sort((a, b) => {
      const aIsEmpty = 0 === a.translation.length;
      const bIsEmpty = 0 === b.translation.length;
      if (aIsEmpty && !bIsEmpty) {
        return -1;
      }
      if (!aIsEmpty && bIsEmpty) {
        return 1;
      }
      return 0;
    });
  }

  findSelectedCustomSegment() {
    return this.group[
      this.attributeSelection.type as GroupCustomSegmentType
    ].find((segment) => segment._id === this.attributeSelection.custom_segment);
  }

  findNewNodeValuesFromImportedColumns() {
    return [...this.importedColumn.values].filter((value) =>
      isEmpty(value.translation),
    );
  }

  addUnmappedValues() {
    const customSegment = this.findSelectedCustomSegment();
    const newNodeValues = this.findNewNodeValuesFromImportedColumns();
    this.populateSegmentValuesDrawerService
      .openDrawer({
        customSegment,
        group: this.group,
        isPrivateSegment: this.isPrivateSegment(this.attributeSelection.type),
        values: newNodeValues,
      })
      .afterClose.pipe(
        mergeMap(() => this.groupsStoreService.getGroupFromStore(this.group)),
        tap((updateGroupState: GroupsState) => {
          const attributeSelectionType = this.attributeSelection
            .type as GroupCustomSegmentType;
          const groupCustomSegmentsBeforeUpdate =
            this.group[attributeSelectionType];
          const groupCustomSegmentsAfterUpdate =
            updateGroupState.document[attributeSelectionType];
          const customSegmentBeforeUpdate =
            groupCustomSegmentsBeforeUpdate.find(
              (segment) => segment._id === customSegment._id,
            );
          const customSegmentAfterUpdate = groupCustomSegmentsAfterUpdate.find(
            (segment) => segment._id === customSegment._id,
          );
          const difference = differenceBy(
            customSegmentAfterUpdate.nodes,
            customSegmentBeforeUpdate.nodes,
            'name',
          );
          difference.forEach(({ name, _id }) => {
            this.valueOptions.push({
              label: name,
              value: _id,
            });
          });
          this.group = updateGroupState.document;
          this.mapAllValues();
        }),
      )
      .subscribe();
  }

  findSectionType(group: Group, translation: string) {
    const isAccountField = accountFieldOptions.find(
      (field) => translation === field.value,
    );
    if (isAccountField) {
      return accountFields;
    }
    const isGroupField = groupFieldOptions.find(
      (field) => translation === field.value,
    );
    if (isGroupField) {
      return groupFields;
    }
    return group.custom_segments.find((segment) => translation === segment._id)
      ? 'custom_segments'
      : 'hidden_custom_segments';
  }

  setValueOptions(section: string, selection: string) {
    if (accountFields === section) {
      this.attributeSelection.type = accountFields;
      this.isAutomaticallyMappedField = true;
      this.valueOptions = [];
      return;
    }
    this.isAutomaticallyMappedField = false;
    if (groupFields === section) {
      this.attributeSelection.type = groupFields;
      if (role === selection) {
        this.valueOptions = this.group.roles.map((role) => ({
          label: role.name,
          value: role._id,
        }));
        return;
      }
      this.valueOptions = (this.group as any)[selection].map((option: any) => ({
        label: option,
        value: option,
      }));
      return;
    }
    this.attributeSelection.type = section;
    this.attributeSelection.custom_segment = selection;
    const segment = this.group[section as GroupCustomSegmentType]?.find(
      (customSegment) => selection === customSegment._id,
    );
    this.valueOptions = (segment?.nodes ?? []).map((node) => ({
      label: node.name,
      value: node._id,
    }));
  }

  compareImportedColumn(
    a: MappingOption | SelectionType,
    b: MappingOption | SelectionType,
  ) {
    if (!a || !b) {
      return false;
    }
    if ((a as SelectionType)?.selection?.value) {
      return (
        (b as MappingOption)?.value ===
          (a as SelectionType)?.selection?.value ||
        (a as SelectionType)?.selection?.value ===
          (b as SelectionType)?.selection?.value
      );
    }
    if ((a as MappingOption)?.value) {
      return (
        (a as MappingOption)?.value === (b as SelectionType)?.selection?.value
      );
    }
    return false;
  }

  compareSelectedValues(a: MappingOption, b: MappingOption) {
    if (!a || !b) {
      return false;
    }
    return a.value === b.value;
  }

  updateAttributeSelection(value: {
    section: string;
    selection: MappingOption;
  }) {
    if (!value) {
      this.attributeSelection.values = [];
      return;
    }
    this.attributeSelection.translation = value.selection.value;
    this.attributeSelection.values = [];
    this.selected = value.selection;
    this.setValueOptions(value.section, value.selection.value);
    this.hasBeenUpdated = true;
    this.hasUnmappedValues = 'account_fields' !== value.section;
  }

  updateValueSelection(value: string, attributeValue: MappingOption) {
    const valueBody = {
      name: value,
      translation: attributeValue?.value ? [attributeValue.value] : [],
      translationValues: attributeValue?.label ? [attributeValue.label] : [],
    };
    const valueIndex = (this.attributeSelection.values || []).findIndex(
      (attributeValue) => value === attributeValue.name,
    );
    if (0 <= valueIndex) {
      this.hasBeenUpdated = true;
      this.attributeSelection.values[valueIndex] = valueBody;
      this.hasUnmappedValues = !this.attributeSelection.values.every(
        (value) => !isEmpty(value.translation),
      );
      return;
    }
    this.attributeSelection.values.push(valueBody);
    this.hasUnmappedValues = !this.attributeSelection.values.every(
      (value) => !isEmpty(value.translation),
    );
    this.hasBeenUpdated = true;
  }

  updateUniqueColumn(attributeId: string, group: Group) {
    this.importedColumn = {
      ...this.importedColumn,
      attributeId,
      translation: this.attributeSelection.translation,
      values: this.attributeSelection.values,
    };
    return this.closeDrawer(group, this.importedColumn);
  }

  getDestinationPath(group: Group, attributeSelection: AttributeSelection) {
    if (accountFields === attributeSelection.type) {
      return attributeSelection.translation;
    }
    if (groupFields === attributeSelection.type) {
      return `groups.${attributeSelection.translation}`;
    }
    const translation = findCustomSegment(
      group,
      attributeSelection.custom_segment,
    );
    const hiddenCustomSegmentIds = group.hidden_custom_segments?.map(
      (customSegment) => customSegment._id,
    );
    return hiddenCustomSegmentIds?.includes(translation?._id)
      ? 'groups.hidden_segment_selections.node'
      : 'groups.segment_selections.node';
  }

  buildValueTransformations(): ValueTransformations[] {
    if (accountFields === this.attributeSelection.type) {
      return [];
    }
    return this.attributeSelection.values?.map(
      (value) =>
        ({
          destination_value: value.translation,
          source_value: value.name,
        }) as ValueTransformations,
    );
  }

  getUnmappedValues(): string[] {
    const originalMissingValues = this.importedColumn.values.reduce(
      (acc, value) => (isEmpty(value.translation) ? [...acc, value.name] : acc),
      [],
    );
    if (isEmpty(this.attributeSelection.values)) {
      return originalMissingValues;
    }
    const currentlyMapped = this.attributeSelection.values.reduce(
      (acc, value) =>
        !isEmpty(value.translation) ? [...acc, value.name] : acc,
      [],
    );
    return originalMissingValues.reduce(
      (acc, valueName) =>
        !currentlyMapped.includes(valueName) ? [...acc, valueName] : acc,
      [],
    );
  }

  openHasMissingMappingDialog() {
    if (!this.hasUnmappedValues) {
      return of(true);
    }
    const unmappedValues = this.getUnmappedValues();
    return this.listValuesDialog
      .openDialog({
        cancelButton: $localize`:@@attributeMappingDrawer.unmappedValues.cancelButton:Map Values`,
        confirmButton: $localize`:@@attributeMappingDrawer.unmappedValues.confirmButton:Continue without mapping`,
        content: $localize`:@@attributeMappingDrawer.unmappedValues.body:You have unmapped CSV values. Unmapped CSV values will not be imported to your 10KC Hub.`,
        list: unmappedValues,
        tableTitle: $localize`:@@attributeMappingDrawer.unmappedValues.tableTitle:Unmapped CSV values`,
        title: $localize`:@@attributeMappingDrawer.unmappedValues.title:CSV values without mapping`,
      })
      .afterClosed();
  }

  submitAttributeMapping() {
    if (
      !this.hasBeenUpdated &&
      !this.hasSourcePathChanged &&
      !this.hasUnmappedValues
    ) {
      this.closeDrawer(this.group, this.importedColumn);
      return;
    }
    this.openHasMissingMappingDialog()
      .pipe(
        filter((result) => !!result),
        mergeMap((): Observable<{ attributeMapping: AttributeMapping }> => {
          const isCustomSegmentType = [
            'custom_segments',
            'hidden_custom_segments',
          ].includes(this.attributeSelection.type);
          const valueTransformations = this.buildValueTransformations();
          const attributeMapping:
            | AttributeMappingPostBody
            | AttributeMappingPutBody = {
            destination_path: this.getDestinationPath(
              this.group,
              this.attributeSelection,
            ),
            group: this.group._id,
            mapping_type: 'admin-accounts-csv',
            source_path: [this.importedColumn.name],
            value_transformations: valueTransformations,
            ...(isCustomSegmentType && {
              custom_segment: this.attributeSelection.custom_segment,
            }),
          };
          const mappingMethod = this.importedColumn.attributeId
            ? this.groupsBackend.v3.putGroupAttributeMapping(
                attributeMapping,
                this.importedColumn.attributeId,
                this.group._id,
              )
            : this.groupsBackend.v3.postGroupAttributeMapping(
                attributeMapping as AttributeMappingPostBody,
                this.group._id,
              );
          return mappingMethod.pipe(
            catchError((response: HttpErrorResponse) => {
              this.toastService.showError(getServerError(response));
              return EMPTY;
            }),
            tap((response) => {
              const successMessage = $localize`:@@attributeMappingDrawer.savedAttributeMessage:Attribute Mapping has been successfully saved`;
              this.toastService.showSuccess(successMessage);
              this.updateUniqueColumn(
                response.attributeMapping._id,
                this.group,
              );
            }),
            takeUntil(this.destroy$),
          );
        }),
      )
      .subscribe();
  }

  isPrivateSegment(segmentType: string) {
    return 'hidden_custom_segments' === segmentType;
  }

  openAddCustomSegmentDrawer(
    group: Group,
    segmentType: GroupCustomSegmentType,
  ) {
    const isPrivateSegment = this.isPrivateSegment(segmentType);
    const payloadForSegmentsDrawer = {
      group,
      ...(!this.importedColumn.translation &&
        ({
          customSegmentName: this.importedColumn.name,
        } satisfies Partial<PrivateSegmentsDrawerData>)),
    } satisfies PrivateSegmentsDrawerData;
    const segmentDrawer = !isPrivateSegment
      ? this.groupCustomSegmentsDrawerService.openSegmentsDrawer(
          payloadForSegmentsDrawer,
        )
      : this.groupCustomPrivateSegmentsDrawerService.openPrivateSegmentsDrawer(
          payloadForSegmentsDrawer,
        );
    let newCustomSegment: GroupCustomSegmentTypes;
    segmentDrawer
      .pipe(
        filter(
          (groupSegments: GroupCustomSegmentTypes[]) => !isEmpty(groupSegments),
        ),
        mergeMap((groupSegments: GroupCustomSegmentTypes[]) => {
          newCustomSegment = last(groupSegments);
          this.groupOptions[segmentType].push({
            label: newCustomSegment.title,
            value: newCustomSegment._id,
          });
          return this.populateSegmentValuesDrawerService
            .openDrawer({
              isPrivateSegment,
              customSegment: newCustomSegment,
              group: this.group,
              values: this.importedColumn.values,
            })
            .afterClose.pipe(
              mergeMap(() =>
                this.groupsStoreService.getGroupFromStore(this.group),
              ),
            );
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
      )
      .subscribe((updateGroupState: GroupsState) => {
        this.group = updateGroupState.document;
        this.updateAttributeSelection({
          section: segmentType,
          selection: {
            label: newCustomSegment.title,
            value: newCustomSegment._id,
          },
        });
        this.mapAllValues();
      });
  }

  openAddSegmentOptionDrawer(value: string, index: number) {
    const segment = this.group[
      this.attributeSelection.type as GroupCustomSegmentType
    ]?.find(
      (segment: GroupCustomSegmentTypes) =>
        this.attributeSelection.custom_segment === segment._id,
    );
    if (!segment) {
      return;
    }
    const isPrivateSegment = this.isPrivateSegment(
      this.attributeSelection.type,
    );
    this.groupCustomSegmentsDrawerService
      .openSegmentItemDrawer({
        isPrivateSegment,
        customSegment: segment,
        group: this.group,
      })
      .pipe(
        filter(
          (groupCustomSegments: GroupCustomSegmentTypes[]) =>
            !isEmpty(groupCustomSegments),
        ),
        tap((groupCustomSegments: GroupCustomSegmentTypes[]) => {
          const newSegment = groupCustomSegments.find(
            (segment) => this.attributeSelection.custom_segment === segment._id,
          );
          const newMappingOption = last(newSegment?.nodes);
          this.valueOptions.push({
            label: newMappingOption.name,
            value: newMappingOption._id,
          });
          this.group = {
            ...this.group,
            ...{ [this.attributeSelection.type]: groupCustomSegments },
          };
          this.valueSelections[this.pageIndex - 1][index] = {
            label: newMappingOption.name,
            value: newMappingOption._id,
          };
          this.updateValueSelection(value, {
            label: newMappingOption.name,
            value: newMappingOption._id,
          });
        }),
        finalize(() => {
          this.hasMappedAllViableValues = false;
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
      )
      .subscribe();
  }

  openAddNewRoleDrawer(value: string, index: number) {
    this.hubConfigRolesDrawerService
      .openDrawer({
        group: this.group,
        showInviteable: true,
        showOptedIntoMatchmaking: true,
        showSearchable: true,
      })
      .pipe(
        filter((groupRoles: GroupRole[]) => !isEmpty(groupRoles)),
        tap((groupRoles: GroupRole[]) => {
          const newMappingOption = last(groupRoles);
          this.valueOptions.push({
            label: newMappingOption.name,
            value: newMappingOption._id,
          });
          this.valueSelections[this.pageIndex - 1][index] = {
            label: newMappingOption.name,
            value: newMappingOption._id,
          };
          this.updateValueSelection(value, {
            label: newMappingOption.name,
            value: newMappingOption._id,
          });
          this.group = { ...this.group, roles: groupRoles };
        }),
        finalize(() => {
          this.hasMappedAllViableValues = false;
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
      )
      .subscribe();
  }

  hasTranslationBeenSelected() {
    return !this.attributeSelection?.translation;
  }

  mapAllValues() {
    const unmappedValues = this.getUnmappedValues();
    let mappedCounter = 0;
    for (const valueName of unmappedValues) {
      const existingValue = this.valueOptions.find(
        (valueOption) =>
          valueName.toLocaleLowerCase() ===
          valueOption.label.toLocaleLowerCase(),
      );
      if (existingValue) {
        const importedColumIndex = this.importedColumn.values.findIndex(
          (value) => valueName === value.name,
        );
        const attributeSelectionIndex =
          this.attributeSelection.values.findIndex(
            (value) => valueName === value.name,
          ) % 10;
        const index =
          0 <= attributeSelectionIndex
            ? attributeSelectionIndex
            : importedColumIndex % 10;
        const chunkIndex = Math.floor(importedColumIndex / 10);
        this.valueSelections[chunkIndex][index] = {
          label: valueName,
          value: existingValue.value,
        };
        mappedCounter += 1;
        this.updateValueSelection(valueName, existingValue);
      }
    }
    this.hasMappedAllViableValues = true;
    this.hasUnmappedValues = unmappedValues.length > mappedCounter;
  }

  closeDrawer(group: Group, updatedColumn?: VisibleUniqueColumn): void {
    this.drawerRef.close({
      group,
      updatedColumn,
    });
  }
}
